import { useEffect, useState } from 'react';
import { MapPin, Clock, Phone, Globe, ExternalLink } from 'lucide-react';
import { getPlaceDetails, getMapsEmbedUrl, getMapsLinkUrl } from '@/lib/googlePlaces';
import { useMapsKey } from '@/lib/MapsKeyContext';

export default function CafeInfo({ placeId, cafeName }) {
  const { status } = useMapsKey();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!placeId || status !== 'ready') return;
    setLoading(true);
    getPlaceDetails(placeId)
      .then((data) => {
        setDetails(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [placeId, status]);

  const mapsUrl = getMapsLinkUrl(placeId);

  return (
    <div className="space-y-4">
      {/* Map — only render the embed once the key is ready (avoids an empty-key URL) */}
      {status === 'ready' ? (
        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="block rounded-2xl overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow">
          <iframe
            title="cafe-map"
            width="100%"
            height="200"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            src={getMapsEmbedUrl(placeId)}
          />
        </a>
      ) : status === 'error' ? (
        <div className="rounded-2xl border border-border h-[200px] flex items-center justify-center bg-secondary/30">
          <p className="text-sm text-muted-foreground">Map unavailable</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border h-[200px] flex items-center justify-center bg-secondary/30">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {loading && status !== 'error' && (
        <div className="space-y-2 animate-pulse">
          <div className="h-4 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/2" />
        </div>
      )}

      {(error || status === 'error') && (
        <p className="text-xs text-muted-foreground">Could not load cafe details.</p>
      )}

      {details && (
        <div className="space-y-3">
          <h3 className="font-heading text-lg font-semibold">{details.name || cafeName}</h3>

          {details.formatted_address && (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-start gap-2 text-sm text-muted-foreground hover:text-primary transition-colors group">
              <MapPin size={15} className="shrink-0 mt-0.5 group-hover:text-primary" />
              <span className="leading-snug">{details.formatted_address}</span>
              <ExternalLink size={12} className="shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
          )}

          {details.formatted_phone_number && (
            <a href={`tel:${details.formatted_phone_number}`}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
              <Phone size={15} className="shrink-0" />
              {details.formatted_phone_number}
            </a>
          )}

          {details.website && (
            <a href={details.website} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
              <Globe size={15} className="shrink-0" />
              <span className="truncate">{details.website.replace(/^https?:\/\//, '')}</span>
            </a>
          )}

          {details.opening_hours?.weekday_text && (
            <div className="bg-secondary/50 rounded-xl p-3 space-y-1.5">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                <Clock size={13} />
                Opening Hours
              </div>
              {details.opening_hours.weekday_text.map((day, i) => (
                <p key={i} className="text-xs text-foreground/80 leading-relaxed">{day}</p>
              ))}
            </div>
          )}

          {/* Cafe photos from Google */}
          {details.photos && details.photos.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {details.photos.slice(0, 5).map((photo, i) => {
                const url = photo.getUrl?.({ maxWidth: 400 });
                if (!url) return null;
                return (
                  <img
                    key={i}
                    src={url}
                    alt={`Cafe photo ${i + 1}`}
                    className="h-24 w-32 object-cover rounded-xl shrink-0"
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}